/*
 * The sleep timer's slider. One thumb covers 5 minutes to 12 hours, so the stops are not evenly spaced in time:
 * every 5 minutes up to 2 hours, where the choice matters, then every 15 minutes. A phone screen has room for
 * every stop to be a real step under the thumb.
 */
export const TIMER_SHORTCUTS = [15, 30, 60, 120, 240, 480] as const;
export const DEFAULT_TIMER_MINUTES = 30;
export const MAX_TIMER_MINUTES = 720;

const FINE_STEP = 5;
const COARSE_STEP = 15;
const FINE_UNTIL = 120;
const FINE_STOPS = FINE_UNTIL / FINE_STEP - 1;

/** The slider runs from stop 0 to this. */
export const TIMER_LAST_STOP =
    FINE_STOPS + (MAX_TIMER_MINUTES - FINE_UNTIL) / COARSE_STEP;

export const stopToMinutes = (stop: number): number =>
    stop <= FINE_STOPS
        ? FINE_STEP * (stop + 1)
        : FINE_UNTIL + COARSE_STEP * (stop - FINE_STOPS);

/** The nearest stop, for values that are between stops or out of range. */
export const minutesToStop = (minutes: number): number => {
    const clamped = Math.min(MAX_TIMER_MINUTES, Math.max(FINE_STEP, minutes));
    return clamped <= FINE_UNTIL
        ? Math.round(clamped / FINE_STEP) - 1
        : FINE_STOPS + Math.round((clamped - FINE_UNTIL) / COARSE_STEP);
};

/** Hours and minutes of a duration, for the readout. */
export const splitMinutes = (
    minutes: number
): { hours: number; minutes: number } => ({
    hours: Math.floor(minutes / 60),
    minutes: minutes % 60,
});

/** A countdown: m:ss, or h:mm:ss from one hour up. */
export const formatRemaining = (seconds: number): string => {
    const s = Math.max(0, Math.ceil(seconds));
    const p = (n: number) => String(n).padStart(2, '0');
    const hours = Math.floor(s / 3600);
    const mins = Math.floor((s % 3600) / 60);
    return hours > 0
        ? `${hours}:${p(mins)}:${p(s % 60)}`
        : `${mins}:${p(s % 60)}`;
};
